import type { HTMLAttributes, MouseEventHandler, Ref } from 'react'
import { useContext, useState } from 'react'
import Placeholder from '#components/primitives/icons/placeholder.tsx'
import type { LucideIcon } from 'lucide-react'
import { IconVariantContext } from '#components/primitives/icons/create-icon.ts'
import type { IconVariant } from '#components/primitives/icons/create-icon.ts'
import { cn } from '#core/utils/cn.ts'
import { LoadingSpinner } from '#ynt-design-system/components/loading-spinner/index.ts'

export type ThumbnailSize = 'extra-small' | 'small' | 'medium' | 'large'
export type ThumbnailShape = 'rounded' | 'circle'
export type ThumbnailColor =
  | 'neutral'
  | 'brand'
  | 'informative'
  | 'success'
  | 'warning'
  | 'danger'
  | 'teal'
  | 'pink'

export const THUMBNAIL_SHAPE_CLASS: Record<ThumbnailShape, string> = {
  rounded: 'ynt-thumbnail-rounded',
  circle: 'ynt-thumbnail-circle',
}

const COLOR_CLASS: Record<ThumbnailColor, string> = {
  neutral: 'ynt-thumbnail-neutral',
  brand: 'ynt-thumbnail-brand',
  informative: 'ynt-thumbnail-informative',
  success: 'ynt-thumbnail-success',
  warning: 'ynt-thumbnail-warning',
  danger: 'ynt-thumbnail-danger',
  teal: 'ynt-thumbnail-teal',
  pink: 'ynt-thumbnail-pink',
}

const VARIANT_CLASS = {
  loading: 'ynt-thumbnail-loading',
  image: 'ynt-thumbnail-image',
  swatch: 'ynt-thumbnail-swatch',
  icon: 'ynt-thumbnail-icon',
}

/** The ink of a glyph that carries meaning in its colour, e.g. a status. */
export type IconTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger'

const STATUS_CLASS: Record<IconTone, string> = {
  neutral: 'ynt-thumbnail-status-neutral',
  brand: 'ynt-thumbnail-status-brand',
  success: 'ynt-thumbnail-status-success',
  warning: 'ynt-thumbnail-status-warning',
  danger: 'ynt-thumbnail-status-danger',
}

const TONE_CLASS: Record<IconTone, string> = {
  neutral: 'ynt-thumbnail-tone-neutral',
  brand: 'ynt-thumbnail-tone-brand',
  success: 'ynt-thumbnail-tone-success',
  warning: 'ynt-thumbnail-tone-warning',
  danger: 'ynt-thumbnail-tone-danger',
}

export const THUMBNAIL_SIZE_CLASS: Record<ThumbnailSize, string> = {
  'extra-small': 'ynt-thumbnail-extra-small',
  small: 'ynt-thumbnail-small',
  medium: 'ynt-thumbnail-medium',
  large: 'ynt-thumbnail-large',
}

export const THUMBNAIL_COLORS: readonly ThumbnailColor[] = [
  'informative',
  'neutral',
  'brand',
  'success',
  'warning',
  'danger',
  'teal',
  'pink',
]

export interface ThumbnailProps extends Omit<HTMLAttributes<HTMLElement>, 'color' | 'onClick'> {
  /** Image URL. Falls back to the icon when it fails to load. */
  src?: string
  /** A CSS colour to show as a swatch, e.g. in a colour picker. */
  swatch?: string
  icon?: LucideIcon
  /** Draws the icon's fill or line version; defaults to the surrounding `IconVariantContext`, else fill. */
  iconVariant?: IconVariant
  /** The icon's fill and ink. */
  color?: ThumbnailColor
  /** Draws the icon in this ink on a ringed, sunken tile, in place of `color`. */
  tone?: IconTone
  /** Fills a ringed tile with this status colour and draws the icon white, in place of `tone` and `color`; `neutral` stays sunken. */
  status?: IconTone
  size?: ThumbnailSize
  shape?: ThumbnailShape
  loading?: boolean
  /** Renders the thumbnail as a button, which is what turns on the hover state. */
  onClick?: MouseEventHandler<HTMLElement>
  /** `loading` names the thumbnail while it loads and has no `aria-label`. */
  labels?: { loading?: string }
  ref?: Ref<HTMLElement>
}

/** What a host component (Badge, Button) passes through; the host sets size and behaviour. */
export type ThumbnailContent = Pick<
  ThumbnailProps,
  'src' | 'swatch' | 'icon' | 'iconVariant' | 'color' | 'tone' | 'status' | 'shape' | 'loading'
>

/**
 * A small image, colour swatch or icon tile, picked from what is given: `loading`, then a `src`
 * that loads, then `swatch`, then `icon`. Name it with `aria-label`.
 */
export function Thumbnail({
  src,
  swatch,
  icon: Icon = Placeholder,
  iconVariant: iconVariantProp,
  color = 'informative',
  tone,
  status,
  size = 'small',
  shape = 'rounded',
  loading = false,
  onClick,
  labels,
  className,
  style,
  ref,
  'aria-label': ariaLabel,
  ...rest
}: ThumbnailProps) {
  const [failedSrc, setFailedSrc] = useState<string>()
  const inheritedIconVariant = useContext(IconVariantContext)
  const iconVariant = iconVariantProp ?? inheritedIconVariant ?? 'fill'

  const variant = loading
    ? 'loading'
    : src && src !== failedSrc
      ? 'image'
      : swatch
        ? 'swatch'
        : 'icon'

  const Root = onClick ? 'button' : 'span'

  return (
    <Root
      ref={ref as Ref<HTMLButtonElement & HTMLSpanElement>}
      type={onClick ? 'button' : undefined}
      role={onClick ? undefined : 'img'}
      aria-label={ariaLabel ?? (loading ? labels?.loading : undefined)}
      aria-busy={loading || undefined}
      data-variant={variant}
      onClick={onClick}
      className={cn(
        'ynt-thumbnail',
        THUMBNAIL_SIZE_CLASS[size],
        THUMBNAIL_SHAPE_CLASS[shape],
        VARIANT_CLASS[variant],
        variant === 'icon' &&
          (status
            ? cn('ynt-thumbnail-status', STATUS_CLASS[status])
            : tone
              ? cn('ynt-thumbnail-tone', TONE_CLASS[tone])
              : COLOR_CLASS[color]),
        onClick && 'ynt-thumbnail-interactive',
        className,
      )}
      style={variant === 'swatch' ? { ...style, backgroundColor: swatch } : style}
      {...rest}
    >
      {variant === 'image' && <img src={src} alt="" onError={() => setFailedSrc(src)} />}
      {variant === 'icon' && (
        <IconVariantContext value={iconVariant}>
          <Icon className="ynt-thumbnail-glyph" aria-hidden="true" />
        </IconVariantContext>
      )}
      {variant === 'loading' && <LoadingSpinner className="ynt-thumbnail-glyph" />}
    </Root>
  )
}

export default Thumbnail
